import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Sparkles, Target, Mail, CheckCircle2, XCircle, PenLine, FileText, Clock, Download, Phone, MapPin, Save } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatDeShort } from "@/lib/date";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import SectionDeliverablesPanel from "@/components/SectionDeliverablesPanel";
import JobApplicationsPanel from "@/components/JobApplicationsPanel";
import { ClientCertificates } from "@/components/ClientCertificates";
import ClientGoalsPanel from "@/components/ClientGoalsPanel";
import ParticipantLicenses from "@/components/ParticipantLicenses";
import AvgsClientCalendar from "@/components/AvgsClientCalendar";
import { ClientChatPanel } from "@/components/ClientChatPanel";
import { WhatsAppOptIn } from "@/components/WhatsAppOptIn";
import CvProfileSection from "@/components/CvProfileSection";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const STATUS_INFO = {
  proposed: { label: "Vorgeschlagen", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  accepted: { label: "Angenommen ✓", cls: "bg-blue-50 text-blue-800 border-blue-200" },
  rejected: { label: "Abgelehnt", cls: "bg-red-50 text-red-800 border-red-200" },
  completed: { label: "Bitte unterschreiben", cls: "bg-slate-100 text-slate-900 border-slate-300" },
  signed: { label: "Unterschrieben ✓", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};

export default function ClientHomePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [ueSummary, setUeSummary] = useState(null);
  const [report, setReport] = useState(null);
  const [interimReport, setInterimReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signOpen, setSignOpen] = useState(false);
  const [signTarget, setSignTarget] = useState(null);
  const [signature, setSignature] = useState(null);
  const [signing, setSigning] = useState(false);
  const sigRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: clients } = await api.get("/clients");
      const p = Array.isArray(clients) ? clients[0] : null;
      setProfile(p);
      if (p) {
        const [appts, ue, rep, irep] = await Promise.allSettled([
          api.get("/appointments"),
          api.get(`/clients/${p.client_id}/ue-summary`),
          api.get(`/clients/${p.client_id}/final-report`),
          api.get(`/clients/${p.client_id}/interim-report`),
        ]);
        if (appts.status === "fulfilled") setAppointments(appts.value.data);
        if (ue.status === "fulfilled") setUeSummary(ue.value.data);
        if (rep.status === "fulfilled") setReport(rep.value.data);
        if (irep.status === "fulfilled") setInterimReport(irep.value.data);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const respond = async (a, action) => {
    try {
      await api.put(`/appointments/${a.appointment_id}/${action}`);
      toast.success(action === "accept" ? "Termin angenommen" : "Termin abgelehnt");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const openSign = (a) => {
    setSignTarget(a);
    setSignature(null);
    setSignOpen(true);
  };

  const submitSignature = async () => {
    if (!signature) { toast.error("Bitte hier unterschreiben"); return; }
    setSigning(true);
    try {
      await api.post(`/appointments/${signTarget.appointment_id}/sign`, { signature });
      toast.success("Unterschrieben — vielen Dank!");
      setSignOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSigning(false); }
  };

  const downloadReport = async () => {
    if (!profile) return;
    try {
      const res = await api.get(`/clients/${profile.client_id}/final-report/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url; link.download = `Abschlussbericht_${profile.customer_number || profile.client_id}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const downloadInterimReport = async () => {
    if (!profile) return;
    try {
      const res = await api.get(`/clients/${profile.client_id}/interim-report/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url; link.download = `Zwischenbericht_${profile.customer_number || profile.client_id}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const { pending, past } = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return {
      pending: appointments.filter((a) => a.status !== "signed" && (a.date >= today || a.status === "completed")),
      past: appointments.filter((a) => a.status === "signed" || (a.date < today && a.status !== "completed")),
    };
  }, [appointments]);

  return (
    <div className="px-8 py-10 lg:px-12 lg:py-12 max-w-[1000px] mx-auto" data-testid="client-home-root">
      <div className="mb-10 border-b border-slate-200 pb-8">
        <div className="text-xs font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2">
          <Sparkles size={12} strokeWidth={1.5} />
          Willkommen
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mt-2">
          Schön, dass du da bist,<br />{user?.name?.split(" ")[0] || "Teilnehmer/-in"}.
        </h1>
        <p className="text-sm text-slate-500 mt-4 max-w-xl leading-relaxed">
          Hier findest du deine Coaching-Termine und deinen Abschlussbericht. Dein Coach schlägt
          Termine vor — du kannst annehmen, ablehnen und nach dem Termin direkt digital unterschreiben.
        </p>
      </div>

      {loading && (
        <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Lädt…</div>
      )}

      {!loading && profile && (
        <>
          {/* Info + UE-Balance */}
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-3 border border-slate-200 bg-white mb-8"
          >
            <div className="p-6 border-b md:border-b-0 md:border-r border-slate-200">
              <div className="text-xs font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2 mb-3">
                <Calendar size={12} strokeWidth={1.5} /> Maßnahme
              </div>
              <div className="font-heading text-lg font-light tracking-tighter text-slate-900" data-testid="client-period">
                {formatDeShort(profile.start_date)} – {formatDeShort(profile.end_date)}
              </div>
              <div className="text-xs text-slate-500 mt-1 font-mono">
                Kd-Nr {profile.customer_number || "—"} · Maßn. {profile.measure_number || "—"}
              </div>
            </div>
            <div className="p-6 border-b md:border-b-0 md:border-r border-slate-200 md:col-span-2">
              <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-3">UE-Kontingent</div>
              {ueSummary ? (
                <>
                  <div className="flex items-end gap-3">
                    <div className="font-heading text-5xl font-light tracking-tighter text-slate-900" data-testid="client-ue-remaining">
                      {ueSummary.remaining_ue}
                    </div>
                    <div className="text-sm text-slate-500 pb-2">von {ueSummary.total_ue} UE verbleibend</div>
                  </div>
                  <div className="mt-3 h-2 bg-slate-100 relative overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-slate-900 transition-all"
                      style={{ width: `${Math.min(100, ueSummary.usage_pct)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-slate-500 mt-2 font-mono">
                    <span>Verbraucht: {ueSummary.used_ue} UE</span>
                    <span>{ueSummary.usage_pct.toFixed(0)}%</span>
                  </div>
                </>
              ) : (
                <div className="text-sm text-slate-500">Kein Kontingent gesetzt</div>
              )}
            </div>
          </motion.div>

          {/* Zielvereinbarung + zuletzt freigegebener Bericht */}
          <section className="mb-10 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start" data-testid="client-goals-report-row">
            <div className="min-w-0">
              <h2 className="font-heading text-2xl font-light tracking-tighter text-slate-900 mb-4">Zielvereinbarung</h2>
              <ClientGoalsPanel readOnly />
            </div>
            <div className="min-w-0" data-testid="client-latest-report">
              <div className="flex items-baseline justify-between mb-4 gap-2">
                <h2 className="font-heading text-2xl font-light tracking-tighter text-slate-900">
                  {report ? "Abschlussbericht" : interimReport ? "Zwischenbericht" : "Bericht"}
                </h2>
                {(report || interimReport) && (
                  <button
                    data-testid={report ? "client-download-report" : "client-download-interim-report"}
                    onClick={report ? downloadReport : downloadInterimReport}
                    className="text-xs border border-slate-300 px-3 py-2 hover:bg-slate-100 flex items-center gap-1"
                  >
                    <Download size={11} strokeWidth={1.5} /> PDF
                  </button>
                )}
              </div>
              {report || interimReport ? (
                <div className="border border-slate-200 bg-white p-6 max-h-[560px] overflow-y-auto" data-testid={report ? "client-report-content" : "client-interim-report-content"}>
                  <div className="text-xs font-mono text-slate-500 mb-3">
                    Freigegeben am {formatDeShort((report || interimReport).released_at)}
                  </div>
                  <div className="prose prose-sm max-w-none whitespace-pre-wrap text-slate-800 leading-relaxed break-words">
                    {(report || interimReport).content}
                  </div>
                </div>
              ) : (
                <div className="border border-slate-200 bg-white p-8 text-center" data-testid="client-report-empty">
                  <FileText size={24} strokeWidth={1} className="text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-600">Dein Bericht wird von deinem Coach freigegeben und erscheint dann hier.</p>
                </div>
              )}
            </div>
          </section>

          {profile?.client_id && (
            <section className="mb-10" data-testid="client-home-chat">
              <ClientChatPanel clientId={profile.client_id} />
              <div className="mt-3"><WhatsAppOptIn /></div>
            </section>
          )}

          {profile?.profiling_access && (
            <section className="mb-10" data-testid="client-profiling-card">
              <a href="/mein-profiling"
                className="group block border border-slate-900 p-7 hover:bg-slate-50 transition-colors">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Standortbestimmung</div>
                <div className="flex items-end justify-between gap-6 mt-3 flex-wrap">
                  <div>
                    <h2 className="font-heading text-3xl font-light tracking-tighter text-slate-900">Dein Profiling</h2>
                    <p className="text-sm text-slate-600 mt-2 max-w-xl">
                      Halte fest, was du kannst, was dir wichtig ist und was du verdienen willst – analysiere dann
                      konkrete Stellen und sieh, was du schon mitbringst und was dir noch fehlt.
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-2 border border-slate-900 px-5 py-2.5 text-sm group-hover:bg-slate-900 group-hover:text-white transition-colors">
                    <Target size={14} strokeWidth={1.5} /> Jetzt starten
                  </span>
                </div>
              </a>
            </section>
          )}

          {profile?.branding_access && (
            <section className="mb-10" data-testid="client-branding-card">
              <a href="/mein-branding"
                className="group block border border-slate-900 bg-slate-900 text-white p-7 hover:bg-black transition-colors">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Selbstvermarktung</div>
                <div className="flex items-end justify-between gap-6 mt-3 flex-wrap">
                  <div>
                    <h2 className="font-heading text-3xl font-light tracking-tighter">Dein Marken-Guide</h2>
                    <p className="text-sm text-slate-300 mt-2 max-w-xl">
                      Beantworte Schritt für Schritt ein paar Fragen zu dir, lade Bilder und dein Logo hoch und
                      wähle deine Farben – am Ende bekommst du deinen persönlichen Branding-Guide als PDF.
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-2 border border-white/40 px-5 py-2.5 text-sm group-hover:bg-white group-hover:text-slate-900 transition-colors">
                    <Sparkles size={14} strokeWidth={1.5} /> Jetzt starten
                  </span>
                </div>
              </a>
            </section>
          )}

          {/* Kalender – Terminübersicht */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-light tracking-tighter text-slate-900 mb-4">
              Kalender
            </h2>
            <AvgsClientCalendar appointments={appointments} />
          </section>

          {/* CV-Profil */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-light tracking-tighter text-slate-900 mb-4">
              Mein CV-Profil
            </h2>
            <CvProfileSection />
          </section>

          {/* Anstehende / offene Termine */}
          <section className="mb-10">
            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
              <h2 className="font-heading text-2xl font-light tracking-tighter text-slate-900">Deine Termine</h2>
              <div className="flex items-center gap-3">
                <CalendarSubscribeButton />
                <div className="text-xs text-slate-500 font-mono">{pending.length} offen</div>
              </div>
            </div>
            {pending.length === 0 && (
              <div className="border border-slate-200 bg-white p-8 text-center">
                <Calendar size={24} strokeWidth={1} className="text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-600">Aktuell keine offenen Termine. Dein Coach meldet sich, sobald es einen neuen Vorschlag gibt.</p>
              </div>
            )}
            <div className="space-y-3">
              {pending.map((a) => (
                <AppointmentCard key={a.appointment_id} appointment={a} onAccept={() => respond(a, "accept")} onReject={() => respond(a, "reject")} onSign={() => openSign(a)} />
              ))}
            </div>
          </section>

          {/* Historie */}
          {past.length > 0 && (
            <section className="mb-10">
              <h3 className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-3">Anwesenheits-Historie</h3>
              <div className="space-y-2">
                {past.slice(0, 15).map((a) => (
                  <AppointmentCard key={a.appointment_id} appointment={a} compact onAccept={() => respond(a, "accept")} onReject={() => respond(a, "reject")} onSign={() => openSign(a)} />
                ))}
              </div>
            </section>
          )}

          {/* Abschnitts-Ergebnisse (Read-only für TN) */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-light tracking-tighter text-slate-900 mb-4">
              Meine Maßnahme · Fortschritt
            </h2>
            <SectionDeliverablesPanel readOnly />
          </section>

          {profile?.client_id && (
            <section className="mb-10">
              <ClientCertificates clientId={profile.client_id} hideEmpty />
            </section>
          )}

          {/* Job-Track-Liste (TN kann selbst erfassen) */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-light tracking-tighter text-slate-900 mb-4">
              Job-Track-Liste
            </h2>
            <JobApplicationsPanel showExport={false} />
          </section>

          {/* Meine Lizenzen (Software-Zugänge) */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-light tracking-tighter text-slate-900 mb-4">
              Meine Lizenzen
            </h2>
            <ParticipantLicenses lang="de" />
          </section>

          {/* Meine Daten – Selbstpflege */}
          <MyProfileSection profile={profile} onSaved={load} />
        </>
      )}

      {!loading && !profile && (
        <div className="border border-slate-200 bg-white p-10 text-center">
          <p className="text-sm text-slate-600">
            Dein Coach hat dich noch keiner Maßnahme zugeordnet. Bitte melde dich beim Coaching-Team.
          </p>
        </div>
      )}

      <div className="mt-8 flex items-center gap-3 text-xs text-slate-500">
        <Mail size={12} strokeWidth={1.5} />
        Fragen? Schreib deinem Coach oder <a href="mailto:info@we-academy.de" className="underline underline-offset-4 hover:text-slate-900">info@we-academy.de</a>
      </div>

      {/* Signature Dialog */}
      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="max-w-lg rounded-none border border-slate-200 p-0" data-testid="sign-dialog">
          <DialogHeader className="px-8 pt-8 pb-4 border-b border-slate-100">
            <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Unterschrift</div>
            <DialogTitle className="font-heading text-3xl font-light tracking-tighter text-slate-900 mt-1">
              Termin bestätigen
            </DialogTitle>
            <DialogDescription className="sr-only">
              Unterschreibe den Termin, damit die UE gutgeschrieben werden.
            </DialogDescription>
          </DialogHeader>
          <div className="px-8 py-6">
            {signTarget && (
              <div className="mb-4 text-sm text-slate-700">
                <div className="font-mono text-xs text-slate-500">Termin am</div>
                <div className="font-medium">{formatDeShort(signTarget.date)} · {signTarget.time_from || "—"}–{signTarget.time_to || "—"}</div>
                <div className="text-xs text-slate-600 mt-1">{signTarget.planned_hours}h · {signTarget.planned_ue} UE {signTarget.topic ? `· ${signTarget.topic}` : ""}</div>
              </div>
            )}
            <SignaturePad ref={sigRef} label="Deine Unterschrift" onChange={setSignature} testId="client-signature-pad" />
            <p className="text-xs text-slate-500 mt-3 leading-relaxed">
              Mit deiner Unterschrift bestätigst du, dass der Termin wie oben stattgefunden hat.
              Die UE werden anschließend von deinem Kontingent abgezogen.
            </p>
          </div>
          <DialogFooter className="px-8 py-5 border-t border-slate-100 flex gap-3">
            <button data-testid="sign-cancel" onClick={() => setSignOpen(false)} className="px-6 py-3 text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">
              Abbrechen
            </button>
            <button data-testid="sign-submit" onClick={submitSignature} disabled={signing || !signature} className="px-6 py-3 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
              {signing ? "Sende…" : "Unterschreiben"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AppointmentCard({ appointment: a, onAccept, onReject, onSign, compact }) {
  const s = STATUS_INFO[a.status] || STATUS_INFO.proposed;
  return (
    <div className={`bg-white border border-slate-200 p-5 flex flex-wrap items-center gap-4 ${compact ? "opacity-90" : ""}`} data-testid={`client-appt-${a.appointment_id}`}>
      <div className="flex-shrink-0 w-14 text-center border-r border-slate-100 pr-4">
        <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">{a.date.slice(5, 7)}/{a.date.slice(2, 4)}</div>
        <div className="font-heading text-3xl font-light tracking-tighter text-slate-900">{a.date.slice(8, 10)}</div>
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="font-medium text-slate-900">{a.topic || "Coaching-Termin"}</div>
        <div className="mt-1 text-xs text-slate-600 font-mono flex items-center gap-3 flex-wrap">
          {(a.time_from || a.time_to) && <span className="flex items-center gap-1"><Clock size={11} strokeWidth={1.5} />{a.time_from || "?"}–{a.time_to || "?"}</span>}
          <span>{a.planned_hours}h · {a.planned_ue} UE</span>
          <span className="text-slate-400">mit {a.coach_name}</span>
          {a.mode === "online" && (a.meeting_url || a.coach_meet_link) && (
            <a
              href={a.meeting_url || a.coach_meet_link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0a788c] hover:text-[#0a96b4] font-medium"
              title="Meeting öffnen"
            >
              🎥 Zum Meeting
            </a>
          )}
        </div>
      </div>
      <div className={`px-3 py-1 text-xs font-medium border ${s.cls}`}>{s.label}</div>
      {(!compact || a.status !== "signed") && (
        <div className="flex gap-2 flex-shrink-0">
          {!["accepted", "completed", "signed"].includes(a.status) && (
            <>
              <button
                data-testid={`client-accept-${a.appointment_id}`}
                onClick={onAccept}
                className="px-3 py-2 text-xs bg-emerald-700 text-white hover:bg-emerald-800 flex items-center gap-1"
              >
                <CheckCircle2 size={12} strokeWidth={1.5} /> {a.date < new Date().toISOString().slice(0, 10) ? "Nachträglich bestätigen" : "Annehmen"}
              </button>
              {(["proposed", "accepted"].includes(a.status)) && a.date >= new Date().toISOString().slice(0, 10) && (
                <button
                  data-testid={`client-reject-${a.appointment_id}`}
                  onClick={onReject}
                  className="px-3 py-2 text-xs border border-slate-300 text-slate-700 hover:bg-slate-100 flex items-center gap-1"
                >
                  <XCircle size={12} strokeWidth={1.5} /> Ablehnen
                </button>
              )}
            </>
          )}
          {a.status === "completed" && (
            <button
              data-testid={`client-sign-${a.appointment_id}`}
              onClick={onSign}
              className="px-4 py-2 text-xs bg-slate-900 text-white hover:bg-slate-800 flex items-center gap-1"
            >
              <PenLine size={12} strokeWidth={1.5} /> Jetzt unterschreiben
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function MyProfileSection({ profile, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    email: profile?.email || "",
    phone: profile?.phone || "",
    street: profile?.street || "",
    postal_code: profile?.postal_code || "",
    city: profile?.city || "",
  });

  const start = () => {
    setForm({
      email: profile?.email || "",
      phone: profile?.phone || "",
      street: profile?.street || "",
      postal_code: profile?.postal_code || "",
      city: profile?.city || "",
    });
    setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/clients/me/profile", form);
      toast.success("Deine Daten wurden gespeichert");
      setEditing(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <section className="mb-10" data-testid="my-profile-section">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-heading text-2xl font-light tracking-tighter text-slate-900">Meine Daten</h2>
        {!editing && (
          <button
            data-testid="edit-profile-btn"
            onClick={start}
            className="text-xs border border-slate-300 px-3 py-2 hover:bg-slate-100"
          >
            Daten bearbeiten
          </button>
        )}
      </div>
      <div className="border border-slate-200 bg-white p-6">
        <p className="text-xs text-slate-500 mb-5 leading-relaxed">
          Hier kannst du deine Kontaktdaten und Adresse selbst pflegen. Deine Adresse braucht dein Coach für die
          Rechnung an dein Jobcenter / deine Arbeitsagentur.
        </p>
        {!editing ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ProfileField icon={Mail} label="E-Mail" value={profile.email} mono />
            <ProfileField icon={Phone} label="Telefon" value={profile.phone} mono />
            <ProfileField
              icon={MapPin}
              label="Adresse"
              value={
                [profile.street, [profile.postal_code, profile.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || null
              }
              wide
            />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">E-Mail</label>
                <input data-testid="profile-email-input" type="email" className="input-tech font-mono" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <p className="text-[10px] text-slate-500 mt-1">Wenn du die E-Mail änderst, ändert sich auch dein Login.</p>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Telefon</label>
                <input data-testid="profile-phone-input" type="tel" className="input-tech font-mono" placeholder="+49 …" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Adresse</label>
              <input
                data-testid="profile-street-input"
                className="input-tech"
                placeholder="Straße und Hausnummer"
                value={form.street}
                onChange={(e) => setForm({ ...form, street: e.target.value })}
              />
              <div className="grid grid-cols-[120px_1fr] gap-3 mt-3">
                <input
                  data-testid="profile-postal-input"
                  className="input-tech font-mono"
                  placeholder="PLZ"
                  value={form.postal_code}
                  onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                />
                <input
                  data-testid="profile-city-input"
                  className="input-tech"
                  placeholder="Ort"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button data-testid="save-profile-btn" onClick={save} disabled={saving} className="text-sm bg-slate-900 text-white px-6 py-3 hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2">
                <Save size={14} strokeWidth={1.5} /> {saving ? "Speichert…" : "Speichern"}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="text-sm border border-slate-300 text-slate-700 px-6 py-3 hover:bg-slate-100"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function ProfileField({ icon: Icon, label, value, mono, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-1 flex items-center gap-1">
        <Icon size={11} strokeWidth={1.5} /> {label}
      </div>
      <div className={`text-slate-900 ${mono ? "font-mono text-xs" : "text-sm"}`}>
        {value || <span className="text-slate-400 italic">— nicht hinterlegt</span>}
      </div>
    </div>
  );
}

function CalendarSubscribeButton() {
  const [loading, setLoading] = useState(false);
  const [feed, setFeed] = useState(null);

  const open = async () => {
    if (feed) { setFeed({ ...feed, visible: true }); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/calendar/my-feed");
      setFeed({ ...data, visible: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(feed.feed_url);
      toast.success("Kalender-URL kopiert");
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  return (
    <>
      <button
        data-testid="client-calendar-subscribe"
        onClick={open}
        disabled={loading}
        className="text-xs border border-[#0a788c] text-[#0a788c] px-3 py-2 hover:bg-[#e6f3f6] flex items-center gap-2 disabled:opacity-50"
        title="Termine in eigenen Kalender abonnieren"
      >
        <Calendar size={11} strokeWidth={1.5} />
        {loading ? "Lädt…" : "In Kalender abonnieren"}
      </button>

      <Dialog open={!!feed?.visible} onOpenChange={(o) => !o && setFeed(feed ? { ...feed, visible: false } : null)}>
        <DialogContent className="max-w-lg rounded-none border border-slate-200 p-0" data-testid="client-calendar-dialog">
          <DialogHeader className="px-8 pt-8 pb-4 border-b border-slate-100">
            <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Kalender-Abo</div>
            <DialogTitle className="font-heading text-3xl font-light tracking-tighter text-slate-900 mt-1">
              Termine in deinem Kalender
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 mt-1">
              Abonniere diese Adresse in Google Kalender, Apple Kalender oder Outlook — alle deine Coaching-Termine erscheinen dort automatisch.
            </DialogDescription>
          </DialogHeader>
          <div className="px-8 py-6 space-y-5">
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">
                Kalender-Adresse (URL)
              </label>
              <div className="border border-slate-200 bg-slate-50 p-3 text-xs font-mono text-slate-700 break-all">
                {feed?.feed_url}
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                <button
                  onClick={copy}
                  className="text-xs bg-[#0a788c] text-white px-3 py-2 hover:bg-[#0a96b4]"
                >
                  URL kopieren
                </button>
                <a
                  href={feed?.webcal_url}
                  className="text-xs border border-slate-300 text-slate-700 px-3 py-2 hover:bg-slate-100"
                >
                  Direkt öffnen (Apple / Outlook)
                </a>
              </div>
            </div>
            <div className="text-xs text-slate-600 leading-relaxed border-l-2 border-[#0a788c] pl-4">
              <b className="text-slate-900">Google Kalender:</b> Öffne <a href="https://calendar.google.com/calendar/u/0/r/settings/addbyurl" target="_blank" rel="noopener noreferrer" className="text-[#0a788c] underline">calendar.google.com → Andere Kalender → Per URL hinzufügen</a>, füge die URL oben ein.<br />
              <b className="text-slate-900">Apple / Outlook:</b> Klick auf &bdquo;Direkt öffnen&ldquo;.
            </div>
          </div>
          <DialogFooter className="px-8 py-5 border-t border-slate-100">
            <button
              onClick={() => setFeed(feed ? { ...feed, visible: false } : null)}
              className="px-6 py-3 text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              Schließen
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

